import { Button, Icons, LinkButton } from "@flash-tecnologia/hros-web-ui-v2";
import { useCallback, useEffect, useState } from "react";
import { Box } from "src/components/core-components/atoms/Box/box";
import { Focused } from "src/components/core-components/templates/Focused/focused";
import { useNavigate } from "react-router-dom";
import { TrackingEvents } from "./types";
import {
  useAnswerQuestion,
  useGetAvailableQuestions,
  useSession,
} from "@/hooks";
import { setEventTracking } from "@/utils";

import { ModuleAssessment, ModulesUsage } from "./steps";
import { ProgressBarFullWidth } from "@/components/core-components";
import { useModuleAssessmentSelection } from "./hooks/use-module-assessment-selection";
import { useModulesUsage } from "./hooks/use-modules-usage";
import { Question } from "bff/src/services";

enum Steps {
  ASSESSMENT = "assessment",
  MODULES_USAGE = "modules-usage",
}

interface AssessmentWorkflowProps {
  onWorkflowComplete: () => void;
}

export const AssessmentWorkflow: React.FC<AssessmentWorkflowProps> = ({
  onWorkflowComplete,
}) => {
  const navigate = useNavigate();
  const { companyId } = useSession();
  const [step, setStep] = useState<Steps>(Steps.ASSESSMENT);
  const [questionsOrdered, setQuestionsOrdered] = useState<Question[]>([]);

  const moduleAssessment = useModuleAssessmentSelection();
  const modulesUsage = useModulesUsage();

  const { questions, isLoading: isLoadingQuestions } =
    useGetAvailableQuestions(companyId);
  const { answerQuestion, isLoading: isLoadingAnswerQuestion } =
    useAnswerQuestion();

  useEffect(() => {
    if (
      questions &&
      questions.moduleQuestions.length > 0 &&
      questionsOrdered.length === 0
    ) {
      setQuestionsOrdered(questions.moduleQuestions);
    }
  }, [questions]);

  const onAssessmentNext = useCallback(async () => {
    const answeredAssessment = moduleAssessment.selectedOptions.length > 0;
    if (!answeredAssessment || !questions?.moduleAssessment) return;

    await answerQuestion({
      companyId,
      questionId: questions.moduleAssessment.id,
      value: moduleAssessment.selectedOptions.join(";"),
    });

    if (questionsOrdered.length > 0) {
      const reorderedQuestions = moduleAssessment.selectedOptions.reduce(
        (acc, selectedModule) => {
          const questionIndex = acc.remaining.findIndex(
            (question) => question.moduleId === selectedModule
          );

          if (questionIndex !== -1) {
            const [question] = acc.remaining.splice(questionIndex, 1);
            acc.ordered.push(question);
          }

          return acc;
        },
        {
          ordered: [] as Question[],
          remaining: [...questionsOrdered],
        }
      );

      setQuestionsOrdered([
        ...reorderedQuestions.ordered,
        ...reorderedQuestions.remaining,
      ]);
    }
  }, [
    questions,
    moduleAssessment,
    companyId,
    answerQuestion,
    questionsOrdered,
  ]);

  const onModulesNext = useCallback(async () => {
    const usageAnswers = Object.entries(modulesUsage.usageAnswers);
    if (usageAnswers.length > 0) {
      await Promise.all(
        usageAnswers.map(([questionId, value]) =>
          answerQuestion({ companyId, questionId, value })
        )
      );
    }
  }, [modulesUsage.usageAnswers, answerQuestion, companyId]);

  const onNext = useCallback(async () => {
    if (step === Steps.ASSESSMENT) {
      setEventTracking(
        TrackingEvents.SETUP_MODULES_ASSESSMENT_CONTINUE_CLICKED
      );
      await onAssessmentNext();

      if (questions && questions.moduleQuestions.length > 0) {
        return setStep(Steps.MODULES_USAGE);
      }
    }

    if (step === Steps.MODULES_USAGE) {
      setEventTracking(TrackingEvents.SETUP_MODULES_USAGE_CONTINUE_CLICKED);
      await onModulesNext();
    }

    onWorkflowComplete();
  }, [step, questions, onAssessmentNext, onModulesNext, onWorkflowComplete]);

  const onContinueLaterClick = useCallback(() => {
    if (step === Steps.ASSESSMENT) {
      setEventTracking(
        TrackingEvents.SETUP_MODULES_ASSESSMENT_CONTINUE_LATER_CLICKED
      );
    }

    if (step === Steps.MODULES_USAGE) {
      setEventTracking(
        TrackingEvents.SETUP_MODULES_USAGE_CONTINUE_LATER_CLICKED
      );
    }

    navigate("/home");
  }, [navigate, step]);

  if (!questions) return null;

  return (
    <Focused.Root fullScreen>
      <Focused.Header>
        <Focused.Title color="neutral.30">
          Configuração da plataforma
        </Focused.Title>
      </Focused.Header>
      <ProgressBarFullWidth.Root>
        <ProgressBarFullWidth.ActiveBar percent={0} />
        <ProgressBarFullWidth.GuideLine />
      </ProgressBarFullWidth.Root>
      <Focused.Main>
        {step === Steps.ASSESSMENT && (
          <ModuleAssessment
            question={questions.moduleAssessment!}
            selectedOptions={moduleAssessment.selectedOptions}
            onSelectionChange={moduleAssessment.toggleOption}
          />
        )}

        {step === Steps.MODULES_USAGE && (
          <ModulesUsage
            questions={questionsOrdered}
            answers={modulesUsage.usageAnswers}
            onAnswersUsage={modulesUsage.onAnswersUsage}
          />
        )}
      </Focused.Main>
      <Focused.Footer>
        <Box justifyContent="space-between" $width="100%">
          <LinkButton
            variant="neutral"
            size="medium"
            underlined={false}
            onClick={onContinueLaterClick}
          >
            Continuar mais tarde
          </LinkButton>
        </Box>
        <Box gap="xs2">
          <Button
            variant="primary"
            size="medium"
            onClick={onNext}
            loading={isLoadingQuestions || isLoadingAnswerQuestion}
          >
            Continuar
            <Icons name="IconArrowRight" />
          </Button>
        </Box>
      </Focused.Footer>
    </Focused.Root>
  );
};
